// src/components/VoucherGameClient.tsx
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Field from "../components/ui/Field";

import {
  redeemPrintedVoucher,
  validatePrintedVoucher,
  PrizeDto,
} from "../api/printedVoucherGameApi";

import WheelGame from "../pages/VoucherGame/WheelGame";
import GiftBoxGame from "../pages/VoucherGame/GiftBoxGame";

type Step = "ENTER" | "PICK" | "PLAY" | "RESULT";
type Mode = "SPIN" | "GIFTBOX";

const REWARD_STORAGE_KEY = "printedVoucherReward";

function getApiErrorMessage(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  // Backend trả JSON { ok:false, reason, message }
  const backendMsg =
    (typeof data?.message === "string" && data.message) ||
    (typeof data?.error === "string" && data.error) ||
    (typeof data === "string" && data) ||
    "";

  // Ưu tiên status
  if (status === 401) return "Bạn cần đăng nhập để chơi voucher game.";
  if (status === 409) return "Mã vừa bị người khác sử dụng (Conflict).";

  // LOCK_TIMEOUT (bạn có thể trả 429)
  if (status === 429) return backendMsg || "Hệ thống đang bận. Vui lòng thử lại.";
  if (status === 400 && data?.reason === "LOCK_TIMEOUT")
    return backendMsg || "Hệ thống đang bận. Vui lòng thử lại.";

  return backendMsg || e?.message || "Có lỗi xảy ra";
}

export default function VoucherGameClient() {
  const [code, setCode] = useState<string>("");
  const [step, setStep] = useState<Step>("ENTER");
  const [mode, setMode] = useState<Mode | null>(null);

  const [allowed, setAllowed] = useState<string[]>([]);
  const [prize, setPrize] = useState<PrizeDto | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // ✅ hard lock để chặn gọi redeem đồng thời (double click / spam)
  const redeemingRef = useRef(false);

  const canSpin = useMemo(() => allowed.includes("SPIN"), [allowed]);
  const canGiftbox = useMemo(() => allowed.includes("GIFTBOX"), [allowed]);

  const resetAll = () => {
    if (loading || redeemingRef.current) return; // ✅ đang xử lý thì không reset
    setCode("");
    setAllowed([]);
    setPrize(null);
    setMode(null);
    setError("");
    setStep("ENTER");
  };

  const resetToPick = () => {
    if (loading || redeemingRef.current) return;
    setPrize(null);
    setMode(null);
    setError("");
    setStep("PICK");
  };

  // STEP 1: validate code (không redeem ở đây)
  const handleValidate = async () => {
    const c = code.trim();
    if (!c) {
      setError("Vui lòng nhập mã voucher");
      return;
    }

    if (loading) return;

    setLoading(true);
    setError("");

    try {
      const res = await validatePrintedVoucher(c);

      if (!res.ok) {
        setError(res.message || "Mã không hợp lệ");
        return;
      }

      if (res.alreadyRedeemed) {
        setError(
          `Mã này đã được sử dụng${
            res.redeemedAt
              ? ` lúc ${new Date(res.redeemedAt).toLocaleString("vi-VN")}`
              : ""
          }.`
        );
        return;
      }

      const allow = res.allowedExperiences || [];
      setAllowed(allow);

      if (!allow.length) {
        setError("Mã hợp lệ nhưng không có trải nghiệm nào được phép.");
        return;
      }

      // Nếu chỉ có 1 mode -> auto vào chơi
      if (allow.length === 1 && (allow[0] === "SPIN" || allow[0] === "GIFTBOX")) {
        setMode(allow[0] as Mode);
        setStep("PLAY");
      } else {
        setStep("PICK");
      }
    } catch (e: any) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // Redeem: gọi API và trả Prize cho WheelGame/GiftBoxGame
  // ✅ tuyệt đối không throw ra ngoài (để khỏi Uncaught runtime overlay)
  const redeem = async (experience: Mode): Promise<PrizeDto | null> => {
    const c = code.trim();
    if (!c) {
      setError("Thiếu mã voucher");
      return null;
    }

    // ✅ chặn redeem đồng thời
    if (redeemingRef.current) {
      setError("Đang xử lý, vui lòng đợi...");
      return null;
    }

    redeemingRef.current = true;
    setLoading(true);
    setError("");

    try {
      const res = await redeemPrintedVoucher(c, experience);

      if (!res.ok) {
        setError(res.message || "Đổi thưởng thất bại");
        return null;
      }

      if (!res.prize) {
        setError("Không nhận được phần thưởng (prize=null)");
        return null;
      }

      setPrize(res.prize);
      localStorage.setItem(REWARD_STORAGE_KEY, JSON.stringify(res.prize));
      setStep("RESULT");

      return res.prize;
    } catch (e: any) {
      setError(getApiErrorMessage(e));
      return null;
    } finally {
      redeemingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-120px)] bg-brand-ivory flex justify-center px-6 py-16">
      <div className="w-full max-w-[520px]">
        {/* BRAND HEAD */}
        <div className="mb-10">
          <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
            Voucher experience
          </p>
          <h1 className="mt-4 text-[22px] uppercase tracking-[0.35em]">
            Voucher Game
          </h1>
          <div className="mt-6 h-px w-12 bg-black/20" />
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-6 rounded-2xl border border-black/10 bg-white/60 px-4 py-3">
            <p className="text-[11px] leading-relaxed text-black/70">
              <span className="font-medium text-black">Lỗi:</span> {error}
            </p>
          </div>
        )}

        {/* CARD */}
        <div className="rounded-[28px] border border-black/10 bg-white/50 backdrop-blur px-5 sm:px-7 py-7 shadow-sm">
          {/* STEP ENTER */}
          {step === "ENTER" && (
            <div className="space-y-8">
              <p className="text-[11px] leading-relaxed text-black/50">
                Nhập mã voucher in sẵn để mở trải nghiệm. Mã hợp lệ sẽ cho phép bạn{" "}
                <span className="text-black/70 font-medium">Vòng quay</span> hoặc{" "}
                <span className="text-black/70 font-medium">Hộp quà</span>.
              </p>

              <Field label="Mã voucher" error={error || undefined}>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleValidate();
                  }}
                  disabled={loading}
                  placeholder="VD: OWL-XXXX-XXXX"
                  className="lux-input"
                />
              </Field>

              <button
                type="button"
                onClick={handleValidate}
                disabled={loading}
                className={`
                  w-full h-10 rounded-full
                  text-[9.5px] uppercase tracking-[0.55em]
                  transition-all duration-300
                  ${
                    loading
                      ? "bg-black/20 text-black/40 cursor-not-allowed"
                      : "bg-brand-brown text-brand-ivory hover:opacity-95"
                  }
                `}
              >
                {loading ? "Đang kiểm tra..." : "Tiếp tục"}
              </button>

              <div className="pt-2 text-center">
                <Link
                  to="/"
                  className="text-[9px] uppercase tracking-[0.4em] text-black/40 hover:text-black transition"
                >
                  Về trang chủ
                </Link>
              </div>
            </div>
          )}

          {/* STEP PICK */}
          {step === "PICK" && (
            <div className="space-y-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.45em] text-black/35">
                  Chọn trải nghiệm
                </p>
                <p className="mt-3 text-[12px] leading-relaxed text-black/55">
                  Mã voucher của bạn cho phép những chế độ sau. Chọn một chế độ để bắt đầu.
                </p>
              </div>

              <div className="grid gap-3">
                {canSpin && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      if (loading) return;
                      setMode("SPIN");
                      setStep("PLAY");
                    }}
                    className={`
                      w-full h-10 rounded-full
                      text-[9.5px] uppercase tracking-[0.55em]
                      transition-all duration-300
                      ${
                        loading
                          ? "bg-black/20 text-black/40 cursor-not-allowed"
                          : "bg-brand-brown text-brand-ivory hover:opacity-95"
                      }
                    `}
                  >
                    🎡 Quay vòng quay
                  </button>
                )}

                {canGiftbox && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      if (loading) return;
                      setMode("GIFTBOX");
                      setStep("PLAY");
                    }}
                    className={`
                      w-full h-10 rounded-full
                      text-[9.5px] uppercase tracking-[0.55em]
                      transition-all duration-300
                      ${
                        loading
                          ? "border border-black/15 bg-white/30 text-black/40 cursor-not-allowed"
                          : "border border-black/20 bg-white/60 text-black/70 hover:bg-white/80"
                      }
                    `}
                  >
                    🎁 Mở hộp quà
                  </button>
                )}

                <button
                  type="button"
                  onClick={resetAll}
                  disabled={loading}
                  className="w-full h-10 rounded-full text-[9px] uppercase tracking-[0.45em] text-black/40 hover:text-black transition disabled:opacity-50"
                >
                  Nhập mã khác
                </button>
              </div>

              <div className="pt-2 text-center">
                <p className="text-[9px] uppercase tracking-[0.35em] text-black/30">
                  OWL DREAM · Dream in the dark
                </p>
              </div>
            </div>
          )}

          {/* STEP PLAY */}
          {step === "PLAY" && mode && (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
                    Now playing
                  </p>
                  <h2 className="mt-3 text-[14px] uppercase tracking-[0.35em] text-black/80">
                    {mode === "SPIN" ? "Vòng quay" : "Hộp quà"}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={resetToPick}
                  disabled={loading}
                  className="text-[9px] uppercase tracking-[0.4em] text-black/40 hover:text-black transition disabled:opacity-50"
                >
                  Đổi chế độ
                </button>
              </div>

              {/* game area */}
              <div className="rounded-2xl border border-black/10 bg-white/40 px-3 py-4">
                {mode === "SPIN" ? (
                  <WheelGame
                    onSpin={async () => {
                      const p = await redeem("SPIN");
                      // ✅ WheelGame đang expect Promise<PrizeDto>, nên nếu fail trả null thì throw nhẹ tại đây?
                      // => tốt nhất: trả Promise.reject để WheelGame handle nội bộ (và WheelGame đừng throw lên nữa)
                      if (!p) return Promise.reject(new Error("REDEEM_FAILED"));
                      return p;
                    }}
                    onDone={(p) => setPrize(p)}
                  />
                ) : (
                  <GiftBoxGame
                    count={9}
                    onPick={async () => {
                      const p = await redeem("GIFTBOX");
                      if (!p) return Promise.reject(new Error("REDEEM_FAILED"));
                      return p;
                    }}
                    onDone={(p) => setPrize(p)}
                  />
                )}
              </div>

              {loading && (
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-black/35">
                    Đang xử lý...
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={resetAll}
                disabled={loading}
                className="w-full h-10 rounded-full border border-black/20 bg-white/60 text-[9.5px] uppercase tracking-[0.55em] text-black/65 hover:bg-white/80 transition disabled:opacity-50"
              >
                Nhập mã khác
              </button>
            </div>
          )}

          {/* STEP RESULT */}
          {step === "RESULT" && prize && (
            <div className="space-y-8">
              <div>
                <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
                  Reward unlocked
                </p>
                <h2 className="mt-4 text-[18px] uppercase tracking-[0.3em]">
                  Chúc mừng
                </h2>
                <div className="mt-5 h-px w-10 bg-black/20" />
              </div>

              <div className="rounded-2xl border border-black/10 bg-white/60 px-4 py-4">
                <p className="text-[11px] leading-relaxed text-black/60">
                  Bạn nhận được:
                </p>
                <p className="mt-2 text-[12px] leading-relaxed text-black">
                  <span className="font-medium">{prize.name}</span>{" "}
                  <span className="text-black/50">
                    ({prize.type}) —{" "}
                    <span className="font-medium text-black/70">{prize.code}</span>
                  </span>
                </p>
              </div>

              <div className="grid gap-3">
                <button
                  type="button"
                  onClick={() => (window.location.href = "/cart")}
                  className="w-full h-10 rounded-full bg-brand-brown text-brand-ivory text-[9.5px] uppercase tracking-[0.55em] hover:opacity-95 transition"
                >
                  Dùng khi mua hàng
                </button>

                <button
                  type="button"
                  onClick={resetAll}
                  disabled={loading}
                  className="w-full h-10 rounded-full border border-black/20 bg-white/60 text-[9.5px] uppercase tracking-[0.55em] text-black/65 hover:bg-white/80 transition disabled:opacity-50"
                >
                  Nhập mã khác
                </button>
              </div>

              <div className="pt-2 text-center">
                <Link
                  to="/"
                  className="text-[9px] uppercase tracking-[0.4em] text-black/40 hover:text-black transition"
                >
                  Về trang chủ
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* FOOT */}
        <p className="mt-10 text-[9px] uppercase tracking-[0.35em] text-black/30">
          Secure play · Private reward
        </p>
      </div>
    </div>
  );
}
